import {memoize} from '@sail/utils';

import type {LocationReturn, LocationSearchObject} from 'src/types';

// Object used to discard all keys that already exist in an empty object
// through Object's prototype, like `__proto__` and others.
const EMPTY = {};

export default memoize(function urlToFullLocation(url: string): LocationReturn {
  const parts = url.match(
    /^(?:https?:\/\/[^/]+)?(\/[^?#]*)(?:\?([^#]*))?(?:#(.*)$)?$/,
  );

  if (!parts) {
    throw new TypeError(`Invalid URL: ${url}`);
  }

  return {
    pathname: parts[1],
    search: parts[2] ? stringToObject(parts[2]) : {},
    hash: parts[3] || '',
  };
});

function stringToObject(string: string): LocationSearchObject {
  const buffer: LocationSearchObject = {};
  const namespaced: {[key: string]: string | string[]} = {};

  string.split('&').forEach((section) => {
    const equal = section.indexOf('=');
    const key = equal === -1 ? section : section.substr(0, equal);
    const value = equal === -1 ? '' : section.substr(equal + 1);
    const decoded = decodeURIComponent(value);

    if (!key && !decoded) {
      return;
    }

    const current = namespaced[key];

    if (current instanceof Array) {
      current.push(decoded);
    } else if (typeof current === 'string') {
      namespaced[key] = [current, decoded];
    } else {
      namespaced[key] = decoded;
    }
  });

  for (const key in namespaced) {
    if (namespaced.hasOwnProperty(key)) {
      const value = namespaced[key];

      const decodedKey = decodeURIComponent(key);

      const path = (decodedKey.match(/^[^[\]]*/) || [''])
        .concat((decodedKey.match(/\[.*?\]/g) || []).map((k) => k.slice(1, -1)))
        .map((k) => decodeURIComponent(k));

      if (value instanceof Array && path[path.length - 1] === '') {
        path.pop();
      }

      let item: LocationSearchObject = buffer;

      for (let i = 0; i < path.length; i++) {
        let key = path[i];

        if (key in EMPTY) {
          break;
        }

        if (key === '') {
          key = item instanceof Array ? `${(item.length || 1) - 1}` : '0';
        }

        if (i === path.length - 1) {
          Object.assign(item, {[key]: value});
        } else if (!item[key]) {
          Object.assign(item, {[key]: /^\d*$/.test(path[i + 1]) ? [] : {}});
        }

        // nosemgrep: prototype-pollution-loop
        item = item[key] as LocationSearchObject;
      }
    }
  }

  return buffer;
}
